import { IconButton, Tooltip, Typography } from "@mui/material";
import ColumnContainer from "../../../common/ColumnContainer";
import RowContainer from "../../../common/RowContainer";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { TableWrapper, TitleContainer, Wrapper } from "../../style";
import { Divider, Grid } from "@mui/material";
import { Add, ArrowBackIosRounded, Download } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { selectAuthData } from "../../../../redux/features/auth/authSlice";
import { DashboardButton } from "../../../common/DashboardButton";
import { AttachFileRounded, SpeakerNotesRounded } from "@mui/icons-material";
import { userDashboardRoute } from "../../../../utils/routes/Routes";
import { useEffect, useState } from "react";
import { handleDownloadClick } from "../../../../utils/helpers/downloadFile";
import { useGetProjectByProjectIdQuery } from "../../../../redux/features/projects/projectsApi";
import { FormatDateTime } from "../../../../utils/helpers/FormatDateTime";
import { getTicketCreating } from "../../../../redux/features/tickets/ticketsSlice";
import { FormattedDate, FormattedMessage } from "react-intl";
import { translateStatusString } from "../../../../utils/helpers/translateStatusString";
import { selectLanguage } from "../../../../redux/features/language/languageSlice";

const SingleProjectPage = () => {
  //INIT
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  //REDUX
  const userData = useSelector(selectAuthData);
  const userId = userData?.userData?.id;
  const adminId = userData?.userData?.admin_id;
  const currentLanguage = useSelector(selectLanguage);

  // STATE
  const [mappedData, setMappedData] = useState(null);

  const {
    data,
    isLoading,
    isError,
    refetch: refetchSingleProject,
  } = useGetProjectByProjectIdQuery(
    {
      projectId,
    },
    {
      skip: !projectId,
    }
  );

  // EFFECTS
  useEffect(() => {
    refetchSingleProject({ projectId });
  }, [userId, projectId, refetchSingleProject, location.pathname]);

  //FUNCTIONS

  const renderTickets = () => {
    return (
      <ColumnContainer halfGap>
        {data?.tickets.map((ticket, index) => {
          return (
            <RowContainer align="center" start minGap>
              <Tooltip title={<FormattedMessage id="tooltip.viewTicket" />}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <SpeakerNotesRounded />
                  <Link to={`${userDashboardRoute.tickets}/${ticket.id}`}>
                    <Typography
                      key={index}
                      variant="p"
                      component="p"
                      sx={{
                        cursor: "pointer",

                        "&:hover": {
                          color: "var(--main-color)",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Ticket #{ticket.id}
                    </Typography>
                  </Link>
                </div>
              </Tooltip>
            </RowContainer>
          );
        })}
      </ColumnContainer>
    );
  };

  const handleGoBackToProjects = () => {
    navigate(`${userDashboardRoute.projects}`);
  };

  const handleGoToNewTicketPage = async () => {
    dispatch(
      getTicketCreating({
        projectId,
        userId,
      })
    );
    navigate(`${userDashboardRoute.newTicket}`);
  };

  const titles = {
    created_at: <FormattedMessage id="project.created" />,
    modified_at: <FormattedMessage id="project.updated" />,
    status: <FormattedMessage id="project.status" />,
    v_type: <FormattedMessage id="project.type" />,
    brand: <FormattedMessage id="project.brand" />,
    model: <FormattedMessage id="project.model" />,
    generations: <FormattedMessage id="project.generation" />,
    engine: <FormattedMessage id="project.engine" />,
    engine_hp: <FormattedMessage id="project.engineHp" />,
    year: <FormattedMessage id="project.year" />,
    gearbox: <FormattedMessage id="project.gearbox" />,
    fuel: <FormattedMessage id="project.fuel" />,
    reading_method: <FormattedMessage id="project.readingMethod" />,
    ecu_brand: <FormattedMessage id="project.ecuBrand" />,
    ecu_name: <FormattedMessage id="project.ecuName" />,
    ecu_type: <FormattedMessage id="project.ecuType" />,
    services: <FormattedMessage id="project.services" />,
    dtc: <FormattedMessage id="project.dtc" />,
  };

  const textfieldProjMapped = (data) => {
    const arrayProjectTextfield = data?.project_textfield.split(", ");

    const mappedDataObj = {
      created_at: <FormatDateTime value={data?.created_at} />,
      modified_at: <FormatDateTime value={data?.modified_at} />,
      status: data?.status,
      v_type: arrayProjectTextfield[0] || "-",
      brand: arrayProjectTextfield[1] || "-",
      model: arrayProjectTextfield[2] || "-",
      generations: arrayProjectTextfield[3] || "-",
      engine: arrayProjectTextfield[4] || "-",
      engine_hp: arrayProjectTextfield[5] || "-",
      year: arrayProjectTextfield[6] || "-",
      gearbox: arrayProjectTextfield[7] || "-",
      fuel: arrayProjectTextfield[8] || "-",
      ecu_brand: arrayProjectTextfield[9] || "-",
      ecu_name: arrayProjectTextfield[10] || "-",
      ecu_type: arrayProjectTextfield[11] || "-",
      services: data?.services,
      dtc: data?.dtc,
    };
    return mappedDataObj;
  };

  const getNestedValue = (data, title) => {
    return data[title]?.name || data[title]?.engine_cv || data[title];
  };

  useEffect(() => {
    if (data) {
      setMappedData(data);
      if (data.project_textfield) {
        const result = textfieldProjMapped(data);

        setMappedData(result);
      }
    }
  }, [data]);

  return (
    <Wrapper className="user-project-detail">
      <ColumnContainer
        style={{ height: "100vh", gap: "3rem", width: "95%" }}
        start
      >
        <ColumnContainer>
          <TitleContainer>
            <ColumnContainer noGap>
              <RowContainer start align="center">
                <RowContainer minGap start align="center">
                  <IconButton>
                    <ArrowBackIosRounded
                      onClick={() => handleGoBackToProjects()}
                    />
                  </IconButton>
                  <Typography variant="h3" component="h3">
                    <FormattedMessage id="project.title.singular" /> #
                    {projectId}
                  </Typography>
                </RowContainer>
              </RowContainer>
            </ColumnContainer>
          </TitleContainer>
        </ColumnContainer>
        <ColumnContainer halfGap sx={{ gap: "4rem" }}>
          {mappedData ? (
            <ColumnContainer>
              <TableWrapper className="projects-details-table">
                <Grid container spacing={2} columnSpacing={6}>
                  {Object.entries(titles).map(([key, titleValue]) => {
                    const correctValue = getNestedValue(mappedData, key);
                    if (!!correctValue) {
                      return (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={6}
                          lg={6}
                          height="fit-content"
                          key={key}
                        >
                          <ColumnContainer minGap>
                            <Typography
                              variant="h6"
                              component="h6"
                              fontSize="1rem"
                              fontWeight="bold"
                              color={"var(--main-color)"}
                            >
                              {titleValue}
                            </Typography>
                            <Typography variant="body1">
                              {key === "created_at" || key === "modified_at" ? (
                                <FormatDateTime value={correctValue} />
                              ) : key === "status" ? (
                                translateStatusString(
                                  correctValue,
                                  currentLanguage
                                )
                              ) : (
                                correctValue.toString()
                              )}
                            </Typography>
                            <Divider
                              sx={{
                                borderColor: "#f5f5f5",
                              }}
                            />
                          </ColumnContainer>
                        </Grid>
                      );
                    }
                    return null;
                  })}
                </Grid>
              </TableWrapper>
              <Grid container spacing={2} columnSpacing={6}>
                <Grid item xs={12} sm={12} md={12} lg={6} height="fit-content">
                  <ColumnContainer halfGap className="attachement">
                    <Typography
                      variant="h6"
                      component="h6"
                      fontSize="1rem"
                      fontWeight="bold"
                      color={"var(--main-color)"}
                    >
                      <FormattedMessage id="project.file" />
                    </Typography>

                    <RowContainer align="center" minGap>
                      {data?.ori_file_id ? (
                        <RowContainer start minGap align="center">
                          <AttachFileRounded
                            sx={{
                              "& path": {
                                color: "var(--text-primary)",
                              },
                            }}
                          />
                          <Typography
                            variant="p"
                            component="p"
                            color={"var(--text-primary)"}
                          >
                            {data?.ori_file_detail?.file_name}
                          </Typography>
                          <Tooltip
                            title={
                              <FormattedMessage id="tooltip.downloadFile" />
                            }
                          >
                            <div>
                              <IconButton
                                className="download-file-btn"
                                onClick={() =>
                                  handleDownloadClick(
                                    false,
                                    userId,
                                    data?.ori_file_id
                                  )
                                }
                              >
                                <Download />
                              </IconButton>
                            </div>
                          </Tooltip>
                        </RowContainer>
                      ) : (
                        <Typography
                          variant="p"
                          component="p"
                          color={"var(--text-primary)"}
                        >
                          -
                        </Typography>
                      )}
                    </RowContainer>
                    <Divider
                      sx={{
                        borderColor: "#f5f5f5",
                      }}
                    />
                  </ColumnContainer>
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={6} height="fit-content">
                  <Typography
                    variant="h6"
                    component="h6"
                    fontSize="1rem"
                    fontWeight="bold"
                    color={"var(--main-color)"}
                  >
                    <FormattedMessage id="project.modFile" />
                  </Typography>

                  <ColumnContainer halfGap className="admin-file">
                    {data?.mod_file_id ? (
                      <RowContainer align="center" minGap>
                        <RowContainer start minGap align="center">
                          <AttachFileRounded
                            sx={{
                              "& path": {
                                color: "var(--text-primary)",
                              },
                            }}
                          />
                          <Typography
                            variant="p"
                            component="p"
                            color={"var(--text-primary)"}
                          >
                            {data?.mod_file_detail?.file_name}
                          </Typography>
                        </RowContainer>
                        <Tooltip
                          title={<FormattedMessage id="tooltip.downloadFile" />}
                        >
                          <div>
                            <IconButton
                              className="download-file-btn"
                              onClick={() =>
                                handleDownloadClick(
                                  false,
                                  userId,
                                  data?.mod_file_id
                                )
                              }
                            >
                              <Download />
                            </IconButton>
                          </div>
                        </Tooltip>
                      </RowContainer>
                    ) : (
                      <Typography
                        variant="p"
                        component="p"
                        color="var(--text-secondary)"
                      >
                        <FormattedMessage id="project.noModFile" />
                      </Typography>
                    )}
                  </ColumnContainer>
                </Grid>
              </Grid>
              <Grid container spacing={2} columnSpacing={6}>
                <Grid item xs={12} sm={12} md={12} lg={6} height="fit-content">
                  <ColumnContainer halfGap className="ticket-list">
                    <RowContainer align="center">
                      <Typography
                        variant="h6"
                        component="h6"
                        fontSize="1rem"
                        fontWeight="bold"
                        color={"var(--main-color)"}
                      >
                        <FormattedMessage id="project.tickets" />
                      </Typography>
                      <DashboardButton
                        type="filled"
                        onClick={handleGoToNewTicketPage}
                      >
                        <Add />
                        <Typography
                          variant="p"
                          component="p"
                          color="var(--primary-text)"
                        >
                          <FormattedMessage id="button.createTicket" />
                        </Typography>
                      </DashboardButton>
                    </RowContainer>
                    {data?.tickets?.length > 0 ? (
                      renderTickets()
                    ) : (
                      <FormattedMessage id="project.noTickets" />
                    )}
                  </ColumnContainer>
                </Grid>
              </Grid>
            </ColumnContainer>
          ) : isLoading ? (
            <Typography variant="h6" component="h6">
              Loading...
            </Typography>
          ) : isError ? (
            <Typography variant="h6" component="h6">
              Error
            </Typography>
          ) : null}
        </ColumnContainer>
      </ColumnContainer>
    </Wrapper>
  );
};
export default SingleProjectPage;
